import { dependsOn, triggerOf } from '@lib/customization/poiDesign';
import {
  afterBlockOf, allFieldsOf, candidatesOf, copyOf, draftIssues, draftOf, fieldsIn, followUpCounts, followUpPosition,
  followUpsOf, insertField, locate, moveBlock, moveField, moveGroup, newField, newFollowUp, newGroup, patchField,
  patchGroup, removeFields, removeGroup, stepStatusCandidate, untranslatedCount, updateField, type Draft,
  type Position,
} from '@lib/customization/poiDraft';
import { keptDraftsOf, useAskBeforeLeaving } from '@lib/customization/unsavedWork';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { Box, Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { resolveLabel, type EffectivePoiType, type LocalizedText, type PoiField, type PoiType } from '@store/api/poiTypeEndpoints';
import { usePermission } from '@store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import DeleteDialog from './DeleteDialog';
import DesignCanvas, { type CanvasActions, type Selection } from './DesignCanvas';
import { designerLayoutStyles, inspectorStyles, segmentedStyles, toolbarStyles } from './designer.styles';
import { issueWords } from './describe';
import FieldInspector from './FieldInspector';
import { type Languages } from './FieldHeading';
import GroupInspector from './GroupInspector';
import JsonEditor from './JsonEditor';
import TryForm from './TryForm';

interface Props {
  poiType: PoiType;
  /** Chosen by the page: its Designer and JSON tabs, and its Try it button. */
  mode: 'design' | 'try' | 'json';
  /** The resolved schema of a type that inherits: ancestors' fields can be triggers too. */
  effective?: EffectivePoiType;
  canEdit: boolean;
  saving: boolean;
  /**
   * Writes the whole type as every edit on the page is written, claiming `version`: the
   * version the draft was taken from, so an edit made elsewhere since is a conflict, not an
   * overwrite. Resolves with the server's refusal, if any.
   */
  onSave: (draft: Draft, version: number) => Promise<{ ok: boolean; message?: string }>;
}

/** The saved copy a draft started from, as text, and its version: what a save claims. */
interface Base {
  json: string;
  version: number;
}

/**
 * Unsaved drafts by type id, for as long as the tab lives. The designer unmounts on any in-app
 * navigation and the router cannot ask first, so without this a click on the sidebar threw the
 * work away; with it the draft is there again when the type is reopened. A kept draft keeps the
 * version it started from, so a type saved elsewhere meanwhile is a conflict on save, as it is
 * while the designer stays open. They are their author's alone: signing out does not reload
 * the tab, so whoever signs in next starts with none. The catalogue marks a type that has
 * one, and a reload or a close of the tab asks first while one is kept.
 */
const keptDrafts = keptDraftsOf<{ draft: Draft; base: Base }>('poi-type');

/**
 * The POI type designer: the form as the inspector sees it, edited in place.
 *
 * Holds a working copy and saves it as one PUT through the page, so the stored shape is the
 * one the field drawers write: `fields`, `groups`, and rules on the dependent entry. Only how
 * a type is authored changes; nothing a device reads does.
 */
const PoiTypeDesigner: React.FC<Props> = ({ poiType, mode, effective, canEdit, saving, onSave }) => {
  const { t } = useLocalization();
  const { userId } = usePermission();

  const locales = useCustomizationLocales();
  // Which language the form is previewed in. Labels are still edited in the primary one.
  const [previewLocale, setPreviewLocale] = useState(locales.primary);
  const languages = useMemo((): Languages => {
    const locale = locales.enabled.includes(previewLocale) ? previewLocale : locales.primary;

    return {
      locale,
      primary: locales.primary,
      resolve: (text?: LocalizedText) => resolveLabel(text, { primary: locales.primary, preferred: locale }),
    };
  }, [locales.enabled, locales.primary, previewLocale]);
  const { resolve } = languages;

  // A kept draft is taken up only by whoever left it.
  const [kept] = useState(() => keptDrafts.read(userId, poiType._id));
  const [draft, setDraft] = useState<Draft>(() => kept?.draft ?? draftOf(poiType));
  // The saved copy the draft started from, and its version: what a save claims.
  const [base, setBase] = useState<Base>(() => kept?.base
    ?? { json: JSON.stringify(draftOf(poiType)), version: poiType.version });
  const [selection, setSelection] = useState<Selection>(null);
  const [deleting, setDeleting] = useState<{ kind: 'field' | 'group'; id: string } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  // The JSON editor owns its text while open; a new key starts it again from the draft.
  const [jsonKey, setJsonKey] = useState(0);

  const dirty = JSON.stringify(draft) !== base.json;

  const reset = () => {
    setDraft(draftOf(poiType));
    setBase({ json: JSON.stringify(draftOf(poiType)), version: poiType.version });
    setJsonKey((key) => key + 1);
  };

  // A newer saved copy, with nothing unsaved here: take it. Unsaved work is never replaced;
  // saving it claims the old version, so the server answers with a conflict instead.
  useEffect(() => {
    if (!dirty) reset();
    // Only the server copy drives this; `dirty` is read, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiType]);

  useEffect(() => {
    if (dirty) keptDrafts.keep(userId, poiType._id, { draft, base });
    else keptDrafts.drop(userId, poiType._id);
  }, [userId, dirty, draft, base, poiType._id]);

  // Reloading or closing the tab is the one way a draft is lost, so that asks first: here
  // for the designer on screen. Once it has been left the same is asked for the draft it kept.
  useAskBeforeLeaving(dirty);

  // A save can end after the designer was left; it then has to say so where the draft is kept.
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const inherited = useMemo(
    () => (effective?.id === poiType._id ? effective.items.filter((entry) => entry.inheritedFrom) : []),
    [effective, poiType._id],
  );
  const candidates = useMemo(
    () => new Map(candidatesOf(draft, inherited, resolve, t('Customization_NewField'), stepStatusCandidate(t))
      .map((candidate) => [candidate.id, candidate])),
    [draft, inherited, resolve, t],
  );
  const issues = draftIssues(draft, candidates);

  /** Puts a new field in, and selects it so its question can be typed straight away. */
  const add = (position: Position | null, field: PoiField) => {
    if (!position) return;

    setDraft((current) => insertField(current, position, field));
    setSelection({ kind: 'field', id: field.id! });
  };

  const actions: CanvasActions = {
    select: setSelection,
    addField: (container, type) => add({ container, index: fieldsIn(draft, container).length }, newField(type)),
    addGroup: () => {
      const group = newGroup();

      setDraft((current) => ({ ...current, groups: [...current.groups, group] }));
      setSelection({ kind: 'group', id: group.id! });
    },
    addFollowUp: (triggerId, condition, type, when) => add(
      followUpPosition(draft, triggerId, condition), newFollowUp(type, condition, when),
    ),
    insertAfter: (id, type) => add(afterBlockOf(draft, id), newField(type)),
    duplicate: (id) => {
      const at = locate(draft, id);

      if (at) add(afterBlockOf(draft, id), copyOf(at.field));
    },
    remove: (id) => setDeleting({ kind: 'field', id }),
    removeGroup: (id) => setDeleting({ kind: 'group', id }),
    moveBlock: (container, from, to) => setDraft((current) => moveBlock(current, container, from, to)),
    moveGroup: (from, to) => setDraft((current) => moveGroup(current, from, to)),
  };

  const save = async () => {
    // Only what the type stores: a stray key pasted into the JSON must not reach the PUT.
    const { name, description, fields, groups } = draft;
    const result = await onSave({ name, description, fields, groups }, base.version);

    setServerError(result.ok ? null : result.message ?? null);

    if (!result.ok) return;

    // The server raises the version by one; the next save claims that until a refetch lands.
    const saved: Base = { json: JSON.stringify(draft), version: base.version + 1 };

    setBase(saved);

    // A save that ends after the designer was left finds no effect to keep its outcome, so it
    // puts it where the way back, the catalogue's mark and the question before a reload all
    // look: nothing if the kept draft is what was saved, else that draft on the new version.
    if (!alive.current) {
      const left = keptDrafts.read(userId, poiType._id);

      if (left) {
        const unsaved = JSON.stringify(left.draft) !== saved.json;

        keptDrafts.leave(userId, poiType._id, unsaved ? { draft: left.draft, base: saved } : undefined);
      }
    }
  };

  // ---- Deleting ----

  const deletedFollowUps = deleting?.kind === 'field' ? followUpsOf(draft, deleting.id) : [];
  const deletedGroup = deleting?.kind === 'group' ? draft.groups.find((group) => group.id === deleting.id) : undefined;
  const deletedIds = deleting?.kind === 'field'
    ? [deleting.id]
    : (deletedGroup?.fields ?? []).flatMap((field) => (field.id ? [field.id] : []));
  // Rules beyond the deleted fields and their own follow-ups that will lose a condition.
  const readers = [...allFieldsOf(draft), ...draft.groups].filter((entry) => !deletedIds.includes(entry.id ?? '')
    && !deletedFollowUps.includes(entry.id ?? '') && dependsOn(entry).some((id) => deletedIds.includes(id))).length;

  const confirmDelete = (withDependents: boolean) => {
    if (!deleting) return;

    // Only direct follow-ups are released; deeper ones still have their own trigger.
    const direct = deletedFollowUps.filter((id) => triggerOf(locate(draft, id)?.field ?? {}) === deleting.id);

    setDraft((current) => {
      if (deleting.kind === 'group') return removeGroup(current, deleting.id);

      return withDependents
        ? removeFields(current, [deleting.id, ...deletedFollowUps])
        : removeFields(current, [deleting.id], direct);
    });
    setSelection(null);
    setDeleting(null);
  };

  // ---- Inspector ----

  const selectedField = selection?.kind === 'field' ? locate(draft, selection.id) : null;
  const selectedGroup = selection?.kind === 'group' ? draft.groups.find((group) => group.id === selection.id) : undefined;

  const inspector = () => {
    if (selectedField) {
      const { field, container } = selectedField;
      const id = field.id!;

      return (
        <FieldInspector
          key={id}
          field={field}
          container={container}
          groupNames={draft.groups.map((group) => resolve(group.name) || t('Customization_NewGroup'))}
          candidates={candidates}
          followUpCounts={followUpCounts(draft, field)}
          languages={languages}
          onChange={(next) => setDraft((current) => updateField(current, id, next))}
          onPatch={(changes) => setDraft((current) => patchField(current, id, changes))}
          onMove={(target) => setDraft((current) => moveField(current, id, target))}
          onAddFollowUp={(answer, type) => actions.addFollowUp(id, answer.condition, type)}
          onDuplicate={() => actions.duplicate(id)}
          onDelete={() => actions.remove(id)}
          disabled={!canEdit}
        />
      );
    }

    if (selectedGroup) {
      const id = selectedGroup.id!;

      return (
        <GroupInspector
          key={id}
          group={selectedGroup}
          candidates={candidates}
          languages={languages}
          onPatch={(changes) => setDraft((current) => patchGroup(current, id, changes))}
          onAddField={() => actions.addField(draft.groups.indexOf(selectedGroup), 'Text')}
          onDelete={() => actions.removeGroup(id)}
          disabled={!canEdit}
        />
      );
    }

    return <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{t('Customization_Designer_NothingSelected')}</Typography>;
  };

  const firstIssue = issues.find((issue) => issue.kind !== 'type' && issue.id);

  return (
    // `clip`, not `hidden`: a hidden overflow is a scroll container, and the inspector's
    // `position: sticky` would stick to this box instead of the page.
    <Paper variant="outlined" sx={{ overflow: 'clip' }}>
      {/* The status changes width as you work, so it sits on the left, where that moves nothing. */}
      <Stack direction="row" sx={toolbarStyles}>
        {dirty && issues.length > 0 && (
          <Button
            size="small" color="warning" sx={{ textTransform: 'none' }}
            onClick={() => firstIssue?.kind !== 'type' && firstIssue?.id && setSelection({ kind: firstIssue.kind, id: firstIssue.id })}
          >
            {t('Customization_Designer_ToFix', { count: new Set(issues.map((issue) => ('id' in issue ? issue.id : 'type'))).size })}
          </Button>
        )}
        <Typography variant="caption" color={dirty ? 'warning.main' : 'text.secondary'}>{dirty ? t('Customization_Designer_Unsaved') : t('Customization_Designer_Saved')}</Typography>
        <Box sx={{ flex: 1 }} />

        {locales.enabled.length > 1 && mode !== 'json' && (
          <>
            <Typography variant="caption" color="text.secondary">{t('Customization_Designer_PreviewIn')}</Typography>
            <ToggleButtonGroup
              exclusive size="small" value={languages.locale} sx={segmentedStyles}
              onChange={(_event, next: string | null) => next && setPreviewLocale(next)}
            >
              {locales.enabled.map((code) => {
                const missing = untranslatedCount(draft, code);

                return (
                  <Tooltip key={code} title={missing ? t('Customization_Designer_MissingTranslations', { count: missing }) : t('Customization_Designer_AllTranslated')}>
                    <ToggleButton value={code}>
                      {code.toUpperCase()}
                      {missing > 0 && <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: 'warning.main' }}>{missing}</Box>}
                    </ToggleButton>
                  </Tooltip>
                );
              })}
            </ToggleButtonGroup>
          </>
        )}

        <Button
          size="small" color="inherit" disabled={!dirty || saving} sx={{ textTransform: 'none' }}
          onClick={() => {
            reset();
            setServerError(null);
          }}
        >
          {t('Customization_Designer_Discard')}
        </Button>
        <Button
          size="small" variant="contained" disableElevation sx={{ textTransform: 'none', fontWeight: 600 }}
          disabled={!canEdit || !dirty || saving || issues.length > 0}
          onClick={() => void save()}
        >
          {t('Common_Save')}
        </Button>
      </Stack>

      {mode === 'json' && (
        <Box sx={{ px: 2, pb: 1 }}>
          <JsonEditor
            key={jsonKey}
            value={draft}
            serverError={serverError}
            issues={issues.flatMap((issue) => issueWords(issue, candidates, resolve) ?? [])}
            disabled={!canEdit}
            onChange={(next) => {
              setServerError(null);
              setDraft(next);
            }}
          />
        </Box>
      )}

      {mode === 'try' && (
        <Box sx={{ p: 2 }}>
          <TryForm draft={draft} inherited={inherited} candidates={candidates} languages={languages} />
        </Box>
      )}

      {mode === 'design' && (
        <Box sx={{ ...designerLayoutStyles, p: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <DesignCanvas
              draft={draft}
              inherited={inherited}
              candidates={candidates}
              selection={selection}
              canEdit={canEdit}
              languages={languages}
              actions={actions}
            />
          </Box>
          <Paper variant="outlined" sx={inspectorStyles} key={selection ? `${selection.kind}:${selection.id}` : 'none'}>
            {inspector()}
          </Paper>
        </Box>
      )}

      {deleting && (
        <DeleteDialog
          kind={deleting.kind}
          name={deleting.kind === 'group'
            ? resolve(deletedGroup?.name) || t('Customization_NewGroup')
            : resolve(locate(draft, deleting.id)?.field.label) || t('Customization_NewField')}
          dependents={deleting.kind === 'group' ? deletedIds.length : deletedFollowUps.length}
          readers={readers}
          onCancel={() => setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}
    </Paper>
  );
};

export default PoiTypeDesigner;
